// =================================================================
// Store: Settings slice (PSD §9.1.6 / TODO Task 1.13, 3.11)
// Persistence: write settings to localStorage on change.
// =================================================================
import type { StateCreator } from 'zustand';
import {
  UnitSystem,
  type AppSettings,
  type AssayConfig,
} from '@/types';
import { DEFAULT_PROTOCOL } from '@/config/defaultProtocol';
import { settingsRepo } from '@/persistence/settingsRepo';
import type { StoreState, SettingsSlice } from './types';

const DEFAULT_SETTINGS: AppSettings = {
  unitSystem: UnitSystem.Conventional,
  protocol: DEFAULT_PROTOCOL,
  educationalMode: false,
  disclaimerAcknowledged: false,
  cdsNoticeDismissed: false,
  assayConfig: { unitLabel: 'mg/L', lloq: 0.5, undetectableHandling: 'flag-only' },
  showLoadDemo: true,
};

export const createSettingsSlice: StateCreator<StoreState, [], [], SettingsSlice> = (set, get) => ({
  settings: (() => {
    const loaded = settingsRepo.load();
    return loaded ? { ...DEFAULT_SETTINGS, ...loaded } : DEFAULT_SETTINGS;
  })(),

  setUnitSystem: (sys) => {
    set((state) => ({ settings: persistSettings({ ...state.settings, unitSystem: sys }) }));
  },
  setEducationalMode: (on) => {
    set((state) => ({ settings: { ...state.settings, educationalMode: on } }));
  },
  setProtocol: (protocol) => {
    set((state) => ({ settings: persistSettings({ ...state.settings, protocol }) }));
    get().scheduleComputeAll();
  },
  setPKParams: (_agent, _params) => {
    // Phase 3: editable PK params per agent (visual config). Recompute triggered.
    get().scheduleComputeAll();
  },
  setAssayConfig: (config: AssayConfig) => {
    set((state) => ({ settings: persistSettings({ ...state.settings, assayConfig: config }) }));
  },
  acknowledgeDisclaimer: () => {
    set((state) => ({
      settings: persistSettings({ ...state.settings, disclaimerAcknowledged: true }),
    }));
  },
  setCdsNoticeDismissed: (dismissed) => {
    set((state) => ({ settings: { ...state.settings, cdsNoticeDismissed: dismissed } }));
  },
  setShowLoadDemo: (show) => {
    set((state) => ({ settings: persistSettings({ ...state.settings, showLoadDemo: show }) }));
  },
});

function persistSettings(settings: AppSettings): AppSettings {
  settingsRepo.save(settings);
  return settings;
}
